/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/InspectPanel.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                         |
 * |---|------------------------|-------------------|------------------|--------------------------------------|
 * | 1 | 2026-06-18 (Phase 3c)  | Claude Sonnet 4.6 | Inspect Mode     | Created — element attribute viewer   |
 * | 2 | 2026-06-19 (fix)       | Claude Sonnet 4.6 | Locator UX       | Always show all 3 locator rows; copy |
 * | 3 | 2026-06-19 (improve)   | Claude Sonnet 4.6 | UX polish        | XPath/UIAutomator generation; section
 *                                                                         reorder; contextual empty labels     |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<InspectPanel />)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. XPath is generated for display + copy only — NOT added to the replay
 *    resolver (requires full XPath tree evaluator, deferred to Phase 5).
 *    Stable XPath uses unique attributes; fragile XPath uses ancestor positions.
 * 2. UIAutomator selector is for developer reference and Appium compatibility.
 * 3. Section order: class/badges → locators → generated selector → other locators →
 *    element info → ancestors → add assertion.
 *
 * =============================================================================
 */

import { useState } from "react";
import { buildSelector } from "../device/recorder.js";
import { generateXPath } from "../device/xpathUtils.js";
import type { XPathQuality } from "../device/xpathUtils.js";
import { generateId } from "@mimiq/ir";
import type { AssertStep, AssertionKind, IRStep } from "@mimiq/ir";
import type { InspectResult } from "../device/inspector.js";
import type { A11yNode } from "../device/treeParser.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InspectPanelProps {
  result: InspectResult | null;
  loading: boolean;
  deviceW: number;
  deviceH: number;
  onAddStep: (step: IRStep) => void;
}

// generateXPath and XPathQuality imported from xpathUtils.ts

function generateUiAutomator(node: A11yNode): string | null {
  if (node.resourceId) return `new UiSelector().resourceId("${node.resourceId}")`;
  if (node.text)       return `new UiSelector().text("${node.text}")`;
  if (node.contentDesc) return `new UiSelector().description("${node.contentDesc}")`;
  return null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function shortClass(className: string): string {
  const parts = className.split(".");
  return parts[parts.length - 1] ?? className;
}

function boundsStr(node: A11yNode): string {
  const { left, top, right, bottom } = node.bounds;
  return `[${left},${top}][${right},${bottom}]`;
}

function boundsWH(node: A11yNode): string {
  const w = node.bounds.right - node.bounds.left;
  const h = node.bounds.bottom - node.bounds.top;
  return `${w} × ${h} px`;
}

const ASSERTION_KINDS: { value: AssertionKind; label: string; needsValue: boolean }[] = [
  { value: "visible",    label: "Visible",     needsValue: false },
  { value: "notVisible", label: "Not Visible", needsValue: false },
  { value: "enabled",    label: "Enabled",     needsValue: false },
  { value: "disabled",   label: "Disabled",    needsValue: false },
  { value: "checked",    label: "Checked",     needsValue: false },
  { value: "unchecked",  label: "Unchecked",   needsValue: false },
  { value: "hasText",    label: "Has Text",    needsValue: true  },
];

// ── Root component ────────────────────────────────────────────────────────────

export function InspectPanel({ result, loading, deviceW, deviceH, onAddStep }: InspectPanelProps) {
  const [assertKind, setAssertKind] = useState<AssertionKind>("visible");
  const [assertValue, setAssertValue] = useState("");

  if (loading) {
    return (
      <div className="inspect-panel">
        <div className="inspect-header">
          <span className="inspect-mode-label">INSPECT MODE</span>
        </div>
        <div className="inspect-loading">
          <span className="inspect-spinner" /> Loading element…
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="inspect-panel">
        <div className="inspect-header">
          <span className="inspect-mode-label">INSPECT MODE</span>
          <span className="inspect-hint">Click any element on the mirror</span>
        </div>
        <p className="inspect-empty">
          No element selected. Click on the phone mirror to inspect a UI element.
          <br /><br />
          Switch back to <strong>Drive</strong> mode to resume recording.
        </p>
      </div>
    );
  }

  const { node, flat, ancestors } = result;

  const selector = buildSelector(node, flat, ancestors, { x: result.deviceX, y: result.deviceY }, { width: deviceW, height: deviceH });
  const needsValue = ASSERTION_KINDS.find((k) => k.value === assertKind)?.needsValue ?? false;

  const { xpath, quality: xpathQuality, note: xpathNote } = generateXPath(node, ancestors, flat);
  const uiAutomator = generateUiAutomator(node);

  function handleAddAssertion() {
    const step: AssertStep = {
      id: generateId(),
      type: "assert",
      selector,
      assertion: assertKind,
      ...(needsValue && assertValue.trim() ? { value: assertValue.trim() } : {}),
    };
    onAddStep(step);
    setAssertValue("");
  }

  return (
    <div className="inspect-panel">
      {/* ── Header ── */}
      <div className="inspect-header">
        <span className="inspect-mode-label">INSPECT MODE</span>
        <span className="inspect-hint">Click another element to switch</span>
      </div>

      {/* ── Class + interactive badges ── */}
      <div className="inspect-classname">
        <span className="inspect-classname-text" title={node.className}>
          {shortClass(node.className)}
        </span>
        <div className="inspect-badges">
          {node.clickable    && <span className="inspect-badge inspect-badge--on">clickable</span>}
          {node.focusable    && <span className="inspect-badge inspect-badge--on">focusable</span>}
          {node.scrollable   && <span className="inspect-badge inspect-badge--on">scrollable</span>}
          {node.longClickable && <span className="inspect-badge inspect-badge--on">long-click</span>}
          {node.enabled      && <span className="inspect-badge inspect-badge--dim">enabled</span>}
          {node.checked      && <span className="inspect-badge inspect-badge--on">checked</span>}
          {node.selected     && <span className="inspect-badge inspect-badge--on">selected</span>}
          {node.password     && <span className="inspect-badge inspect-badge--warn">password</span>}
        </div>
      </div>

      {/* ── ELEMENT LOCATORS ── */}
      <div className="inspect-section-title">Element locators</div>
      <table className="inspect-attrs">
        <tbody>
          <LocatorRow
            label="text"
            value={node.text}
            emptyLabel="no visible text"
            hint="Use 'Text on screen' or 'Text contains' in the locator field"
          />
          <LocatorRow
            label="content-desc"
            value={node.contentDesc}
            emptyLabel="no accessibility label"
            hint="Use 'Content description' — set via android:contentDescription, common on icon buttons"
          />
          <LocatorRow
            label="resource-id"
            value={node.resourceId}
            mono
            emptyLabel="no resource ID"
            hint="Use 'Accessibility ID' — only present on native Android elements, not inside WebView"
          />
        </tbody>
      </table>

      {/* ── GENERATED SELECTOR THAT CAN BE USED IN MIMIQ TOOL ── */}
      <div className="inspect-section-title" style={{ marginTop: "0.75rem" }}>
        Generated selector that can be used in Mimiq tool
      </div>
      {selector.candidates.length === 0 ? (
        <p className="inspect-selector-none">
          No identifier found — would use coordinate fallback ({result.deviceX}, {result.deviceY}).
          Try clicking a text label or a button with an accessibility label.
        </p>
      ) : (
        <ol className="inspect-candidates">
          {selector.candidates.map((c, i) => (
            <li key={i} className="inspect-candidate">
              <span className="inspect-cand-strategy">{c.strategy}</span>
              <span className="inspect-cand-value">{c.value}</span>
              {c.index !== undefined && <span className="inspect-cand-index">[{c.index}]</span>}
            </li>
          ))}
        </ol>
      )}

      {/* ── OTHER LOCATORS (XPath / UIAutomator — for other tools) ── */}
      <div className="inspect-section-title" style={{ marginTop: "0.75rem" }}>
        Other locators (for Appium / UIAutomator)
      </div>
      <table className="inspect-attrs">
        <tbody>
          <LocatorRow
            label="xpath"
            value={xpath}
            mono
            hint="XPath locator for Appium / UIAutomator. Hover quality badge for explanation."
            quality={xpathQuality}
            copyable
          />
          {xpathNote && <tr><td /><td className="inspect-xpath-note">{xpathNote}</td></tr>}
          {uiAutomator ? (
            <LocatorRow
              label="uiautomator2"
              value={uiAutomator}
              mono
              hint="Android UIAutomator selector — use in Appium with driver.findElement(By.android(...))"
              copyable
            />
          ) : (
            <LocatorRow
              label="uiautomator2"
              emptyLabel="no stable selector available"
              hint="Available when element has text, content-desc, or resource-id"
            />
          )}
        </tbody>
      </table>

      {/* ── ELEMENT INFO ── */}
      <div className="inspect-section-title" style={{ marginTop: "0.75rem" }}>Element info</div>
      <table className="inspect-attrs">
        <tbody>
          {node.pkg && <InfoRow label="package" value={node.pkg} mono dim />}
          <InfoRow label="class"  value={node.className} mono dim />
          <InfoRow label="bounds" value={boundsStr(node)} mono />
          <InfoRow label="size"   value={boundsWH(node)} dim />
          <InfoRow label="index"  value={String(node.index)} dim />
        </tbody>
      </table>

      {/* ── Ancestor path ── */}
      {ancestors.length > 0 && (
        <div className="inspect-ancestors-section">
          <div className="inspect-section-title">Ancestors</div>
          <div className="inspect-ancestors">
            {[...ancestors].reverse().map((anc, i) => (
              <span key={i} className="inspect-ancestor">
                {shortClass(anc.className)}
                {anc.resourceId ? <span className="inspect-anc-id"> #{anc.resourceId.split("/").pop()}</span> : null}
                {i < ancestors.length - 1 && <span className="inspect-anc-sep"> › </span>}
              </span>
            ))}
            <span className="inspect-anc-sep"> › </span>
            <span className="inspect-ancestor inspect-ancestor--current">{shortClass(node.className)}</span>
          </div>
        </div>
      )}

      {/* ── Add Assertion ── */}
      <div className="inspect-assert-section">
        <div className="inspect-section-title">Add assertion to steps</div>
        <div className="inspect-assert-form">
          <select
            className="inspect-assert-select"
            value={assertKind}
            onChange={(e) => setAssertKind(e.target.value as AssertionKind)}
          >
            {ASSERTION_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
          {needsValue && (
            <input
              className="inspect-assert-input"
              placeholder="Expected text…"
              value={assertValue}
              onChange={(e) => setAssertValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddAssertion()}
            />
          )}
          <button
            className="btn btn--primary inspect-assert-btn"
            onClick={handleAddAssertion}
            disabled={needsValue && !assertValue.trim()}
          >
            + Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LocatorRow — for element locators and other locators sections ──────────────

function LocatorRow({
  label, value, mono, emptyLabel, hint, quality, copyable,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  emptyLabel?: string;
  hint?: string;
  quality?: XPathQuality;
  copyable?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  function copy() {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  }

  const empty = !value;

  return (
    <tr className={`inspect-attr-row${empty ? " inspect-attr-row--dim" : ""}`}>
      <td className="inspect-attr-label" title={hint ?? label}>{label}</td>
      <td className={`inspect-attr-value${mono ? " inspect-attr-value--mono" : ""}${empty ? " inspect-attr-value--empty" : ""}`} title={value ?? ""}>
        {empty ? (
          <span className="inspect-attr-empty-label">{emptyLabel ?? "not set"}</span>
        ) : (
          <>
            <span className="inspect-attr-text">{value}</span>
            {quality === "indexed" && (
              <span className="inspect-xpath-indexed" title="Uses ancestor anchor + child position index. Stable as long as list order doesn't change."> ⚡ indexed</span>
            )}
            {quality === "fragile" && (
              <span className="inspect-xpath-warn" title="Fragile — breaks when layout changes. Use text or resource-id if available."> ⚠ fragile</span>
            )}
            {copyable && (
              <button className="inspect-copy-btn" onClick={copy} title="Copy value">
                {copied ? "✓" : "⧉"}
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

// ── InfoRow — for element info section (simpler, no copy) ─────────────────────

function InfoRow({ label, value, mono, dim }: { label: string; value: string; mono?: boolean; dim?: boolean }) {
  return (
    <tr className={`inspect-attr-row${dim ? " inspect-attr-row--dim" : ""}`}>
      <td className="inspect-attr-label">{label}</td>
      <td className={`inspect-attr-value${mono ? " inspect-attr-value--mono" : ""}`} title={value}>{value}</td>
    </tr>
  );
}
